import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { errMsg } from "../ui";
import { fmtTime, money } from "./meta";

function ReviewDialog({ r, cycleKey, open, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNote(r.review?.note || ""); }, [open, r.review]);
  const call = async (fn, msg, delta) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(delta); onClose(); }
    catch (e) { toast.error(errMsg(e, "Could not save the review")); } finally { setBusy(false); }
  };
  const save = () => call(() => api.put("/usage-charges/reviews", { cycle_key: cycleKey, domain: r.domain, note: note.trim() }),
    `${r.domain} marked reviewed`, r.review ? 0 : 1);
  const clear = () => call(() => api.delete("/usage-charges/reviews", { params: { cycle_key: cycleKey, domain: r.domain } }),
    "Review removed", -1);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="uc-review-dialog">
        <DialogHeader><DialogTitle>Review {r.domain}</DialogTitle>
          <DialogDescription>{money(r.prev_amount)} last cycle → {money(r.amount)} this cycle. The note stays for every run of this cycle.</DialogDescription></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} placeholder="e.g. New call centre opened — expected"
                  className="text-sm" data-testid="uc-review-note" autoFocus />
        {r.review && <p className="text-[11px] text-slate-500">Reviewed by {r.review.by_name} · {fmtTime(r.review.at)}</p>}
        <DialogFooter className="gap-2">
          {r.review && <Button variant="ghost" className="mr-auto text-rose-600" onClick={clear} disabled={busy} data-testid="uc-review-clear">Remove review</Button>}
          <Button variant="outline" onClick={onClose} data-testid="uc-review-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || note.trim().length < 2} data-testid="uc-review-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Mark reviewed</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Review state of a flagged domain for this cycle. */
export function ReviewCell({ r, cycleKey, canReview, onDone }) {
  const [open, setOpen] = useState(false);
  if (!r.jump) return null;
  const rv = r.review;
  return (
    <>
      {rv ? (
        <button type="button" onClick={() => canReview && setOpen(true)} title={`${rv.by_name} · ${fmtTime(rv.at)}`} data-testid={`uc-review-${r.domain}`}
                className="block max-w-[200px] rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-left text-[11px] text-emerald-800 transition-colors hover:border-emerald-400">
          <span className="flex items-center gap-1 font-semibold"><CheckCircle2 className="h-3 w-3" /> Reviewed · {rv.by_name}</span>
          <span className="line-clamp-2 text-emerald-700">{rv.note}</span>
        </button>
      ) : canReview ? (
        <Button size="sm" variant="outline" className="h-7 border-amber-300 px-2 text-[11px] text-amber-800 hover:bg-amber-50" onClick={() => setOpen(true)} data-testid={`uc-review-${r.domain}`}>
          <MessageSquarePlus className="h-3.5 w-3.5" /> Review</Button>
      ) : <span className="text-[11px] text-amber-700">Not reviewed</span>}
      {canReview && <ReviewDialog r={r} cycleKey={cycleKey} open={open} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}
